// 产品详情页折扣价格获取器 - 优化版
window.ProductDetailPriceFetcher = (function() {
  const DEBUG_MODE = false;
  
  // 获取配置
  const STOREFRONT_TOKEN = window.STOREFRONT_ACCESS_TOKEN || window.ShopifyConfig?.storefrontAccessToken || '';
  const SHOP_DOMAIN = window.Shopify?.shop || window.location.hostname;
  
  // 缓存
  const priceCache = new Map();
  const CACHE_DURATION = 5 * 60 * 1000; // 5分钟
  
  // 请求状态管理
  let isRequesting = false;
  let pendingRequest = null;
  
  function debugLog(message, data = null) {
    if (DEBUG_MODE) {
      console.log(`[ProductDetailPriceFetcher] ${message}`, data || '');
    }
  }

  // 获取当前选中的变体ID
  function getCurrentVariantId() {
    // 尝试多种方式获取变体ID
    const variantId = 
      // 1. 从URL参数获取
      new URLSearchParams(window.location.search).get('variant') ||
      // 2. 从全局变量获取
      window.ShopifyAnalytics?.meta?.selectedVariantId ||
      // 3. 从表单获取
      document.querySelector('input[name="id"]')?.value ||
      // 4. 从产品JSON获取
      window.product?.selected_or_first_available_variant?.id ||
      // 5. 从meta标签获取
      document.querySelector('meta[property="product:variant:id"]')?.content ||
      // 6. 从选中的选项获取
      document.querySelector('.variant-input:checked')?.value ||
      // 7. 从数据属性获取
      document.querySelector('[data-selected-variant]')?.dataset.selectedVariant;
    
    return variantId ? String(variantId) : null;
  }

  // 获取单个变体的折扣价格（单独购物车）
  async function fetchVariantPrice(variantId) {
    if (!variantId) {
      debugLog('未找到变体ID');
      return null;
    }

    debugLog('获取变体价格', { variantId });

    // 检查缓存
    const shortId = variantId.replace('gid://shopify/ProductVariant/', '');
    const cached = priceCache.get(shortId) || priceCache.get(variantId);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      debugLog('使用缓存数据', { variantId });
      return cached.data;
    }

    // 如果正在请求，等待之前的请求完成
    if (isRequesting && pendingRequest) {
      debugLog('等待之前的请求完成');
      try {
        await pendingRequest;
      } catch (e) {
        // 忽略之前请求的错误
      }
    }

    isRequesting = true;

    const query = `
      mutation cartCreate($input: CartInput!) {
        cartCreate(input: $input) {
          cart {
            id
            lines(first: 1) {
              edges {
                node {
                  id
                  quantity
                  merchandise {
                    ... on ProductVariant {
                      id
                      price {
                        amount
                        currencyCode
                      }
                      compareAtPrice {
                        amount
                        currencyCode
                      }
                      product {
                        title
                        handle
                      }
                    }
                  }
                  cost {
                    totalAmount {
                      amount
                      currencyCode
                    }
                    amountPerQuantity {
                      amount
                      currencyCode
                    }
                  }
                  discountAllocations {
                    discountedAmount {
                      amount
                      currencyCode
                    }
                    ... on CartAutomaticDiscountAllocation {
                      title
                      discountedAmount {
                        amount
                        currencyCode
                      }
                    }
                  }
                }
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const variables = {
      input: {
        lines: [{
          merchandiseId: variantId.startsWith('gid://') ? variantId : `gid://shopify/ProductVariant/${variantId}`,
          quantity: 1
        }]
      }
    };

    pendingRequest = (async () => {
      try {
        const response = await fetch(`https://${SHOP_DOMAIN}/api/2024-01/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
          },
          body: JSON.stringify({ query, variables })
        });

        const result = await response.json();
        debugLog('API响应', result);

        if (result.errors) {
          debugLog('GraphQL错误', result.errors);
          return null;
        }

        if (result.data?.cartCreate?.userErrors?.length > 0) {
          debugLog('购物车创建错误', result.data.cartCreate.userErrors);
          return null;
        }

        if (result.data?.cartCreate?.cart?.lines?.edges?.[0]) {
          const line = result.data.cartCreate.cart.lines.edges[0].node;
          
          const originalPrice = parseFloat(line.merchandise.price.amount);
          const compareAtPrice = line.merchandise.compareAtPrice 
            ? parseFloat(line.merchandise.compareAtPrice.amount) 
            : originalPrice;
          const finalPrice = parseFloat(line.cost.totalAmount.amount) / line.quantity;
          const hasDiscount = line.discountAllocations.length > 0;
          
          // 计算折扣
          const discountFromCompareAt = compareAtPrice > finalPrice ? compareAtPrice - finalPrice : 0;
          const discountPercentage = compareAtPrice > finalPrice 
            ? Math.round((discountFromCompareAt / compareAtPrice) * 100) 
            : 0;
          
          const priceData = {
            variantId: line.merchandise.id,
            shortId: shortId,
            originalPrice: originalPrice,
            compareAtPrice: compareAtPrice,
            finalPrice: finalPrice,
            discountAmount: originalPrice - finalPrice,
            discountFromCompareAt: discountFromCompareAt,
            discountPercentage: discountPercentage,
            hasDiscount: hasDiscount || compareAtPrice > finalPrice,
            discountTitle: line.discountAllocations[0]?.title || '',
            currencyCode: line.cost.totalAmount.currencyCode,
            productTitle: line.merchandise.product?.title || '',
            productHandle: line.merchandise.product?.handle || ''
          };
          
          // 缓存结果 - 使用多个键
          const cacheData = { data: priceData, timestamp: Date.now() };
          priceCache.set(shortId, cacheData);
          priceCache.set(variantId, cacheData);
          priceCache.set(line.merchandise.id, cacheData);
          
          return priceData;
        }
      } catch (error) {
        debugLog('获取价格失败', error);
      } finally {
        isRequesting = false;
        pendingRequest = null;
      }
      
      return null;
    })();

    return pendingRequest;
  }

  // 更新产品详情页价格显示
  function updateProductPrice(priceData) {
    if (!priceData) {
      debugLog('没有价格数据，跳过更新');
      return;
    }
    
    debugLog('更新价格显示', priceData);
    
    // 查找价格容器 - 支持多种选择器
    const priceContainer = 
      document.querySelector('.product-price__sale') || 
      document.querySelector('.product-price') ||
      document.querySelector('[data-product-price]');
      
    if (!priceContainer) {
      debugLog('未找到价格容器');
      return;
    }
    
    // 只在有折扣时更新
    if (priceData.hasDiscount && priceData.discountPercentage > 0) {
      // 更新折扣价
      const salePriceElements = priceContainer.querySelectorAll(
        '[data-product-regular-price], .product-price__value--sale, .price--sale'
      );
      salePriceElements.forEach(elem => {
        elem.textContent = formatMoney(priceData.finalPrice, priceData.currencyCode);
      });
      
      // 更新原价
      const comparePriceElements = priceContainer.querySelectorAll(
        '[data-product-compare-price], .product-price__value--compare, .price--compare'
      );
      comparePriceElements.forEach(elem => {
        elem.textContent = formatMoney(priceData.compareAtPrice, priceData.currencyCode);
      });
      
      // 查找或创建折扣标签
      let badgeContainer = priceContainer.querySelector('.product-price__badge');
      
      if (!badgeContainer) {
        // 如果没有折扣标签，创建一个新的
        badgeContainer = document.createElement('span');
        badgeContainer.className = 'product-price__badge product-price__badge--sale p--bold';
        
        // 在价格后面插入标签
        const priceWrapper = priceContainer.querySelector('.product-price__wrapper') || priceContainer;
        priceWrapper.appendChild(badgeContainer);
      }
      
      // 更新折扣标签内容
      badgeContainer.textContent = `${priceData.discountPercentage}% OFF`;
      badgeContainer.classList.add('product-price__badge--sale', 'p--bold');
      
      // 添加折扣类
      priceContainer.classList.add('has-discount-price', 'on-sale');
      
      // 触发自定义事件
      document.dispatchEvent(new CustomEvent('price:updated', {
        detail: priceData
      }));
    }
  }

  // 格式化金额
  function formatMoney(amount, currencyCode) {
    // 使用 Shopify 的货币格式
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(amount * 100, window.Shopify.money_format);
    }
    
    // 备用格式化方法
    return new Intl.NumberFormat(window.Shopify?.locale || 'en-US', {
      style: 'currency',
      currency: currencyCode
    }).format(amount);
  }

  // 监听变体变化
  function watchVariantChanges() {
    let lastVariant = getCurrentVariantId();
    let debounceTimer = null;
    
    const checkVariant = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const currentVariant = getCurrentVariantId();
        if (currentVariant && currentVariant !== lastVariant) {
          lastVariant = currentVariant;
          debugLog('变体已切换', { 
            oldVariant: lastVariant, 
            newVariant: currentVariant 
          });
          fetchAndUpdatePrice();
        }
      }, 100);
    };
    
    // 监听多种事件
    window.addEventListener('popstate', checkVariant);
    document.addEventListener('variant:change', checkVariant);
    document.addEventListener('variant:switch', checkVariant);
    
    // 监听表单变化
    const form = document.querySelector('form[action*="/cart/add"]');
    if (form) {
      form.addEventListener('change', (e) => {
        if (e.target.name === 'id' || e.target.name.includes('option')) {
          checkVariant();
        }
      });
    }
    
    // 监听所有可能的变体选择器
    const selectors = [
      'select[name="id"]',
      'input[name="id"]',
      '.variant-input',
      '[data-variant-input]',
      '.product-form__input input[type="radio"]',
      '.product-form__input select'
    ].join(', ');
    
    document.querySelectorAll(selectors).forEach(selector => {
      selector.addEventListener('change', checkVariant);
    });
    
    // 监听 URL 变化
    let lastUrl = window.location.href;
    const urlObserver = new MutationObserver(() => {
      if (window.location.href !== lastUrl) {
        lastUrl = window.location.href;
        checkVariant();
      }
    });
    
    urlObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // 获取并更新价格
  async function fetchAndUpdatePrice() {
    const variantId = getCurrentVariantId();
    if (!variantId) {
      debugLog('无法获取变体ID');
      return;
    }
    
    // 显示加载状态
    const priceContainer = document.querySelector('.product-price__sale, .product-price');
    if (priceContainer) {
      priceContainer.classList.add('loading');
    }
    
    try {
      const priceData = await fetchVariantPrice(variantId);
      if (priceData) {
        updateProductPrice(priceData);
      }
    } finally {
      // 移除加载状态
      if (priceContainer) {
        priceContainer.classList.remove('loading');
      }
    }
  }

  // 初始化
  function init() {
    if (!STOREFRONT_TOKEN) {
      console.error('[ProductDetailPriceFetcher] Storefront Access Token 未配置');
      return;
    }
    
    // 检查是否在产品详情页
    const isProductPage = 
      document.querySelector('.product-price__sale') || 
      document.querySelector('[data-product-regular-price]') ||
      document.querySelector('.product__info') ||
      window.location.pathname.includes('/products/');
      
    if (!isProductPage) {
      debugLog('不在产品详情页，跳过初始化');
      return;
    }
    
    debugLog('初始化产品详情页价格获取器', {
      token: STOREFRONT_TOKEN.substring(0, 10) + '...',
      shop: SHOP_DOMAIN
    });
    
    // 延迟初始化，确保页面元素加载完成
    setTimeout(() => {
      // 获取初始价格
      fetchAndUpdatePrice();
      
      // 监听变体变化
      watchVariantChanges();
    }, 500);
  }

  return {
    init: init,
    refresh: fetchAndUpdatePrice,
    getCurrentVariant: getCurrentVariantId,
    clearCache: () => {
      priceCache.clear();
      debugLog('缓存已清空');
    },
    testVariant: async (variantId) => {
      const result = await fetchVariantPrice(variantId);
      console.log('测试结果:', result);
      return result;
    },
    getCache: () => priceCache
  };
})();

// DOM 加载完成后初始化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() {
    ProductDetailPriceFetcher.init();
  });
} else {
  ProductDetailPriceFetcher.init();
}

// 支持 Shopify 主题事件
document.addEventListener('shopify:section:load', function() {
  setTimeout(() => ProductDetailPriceFetcher.init(), 100);
});

document.addEventListener('shopify:block:select', function() {
  ProductDetailPriceFetcher.refresh();
});

// 控制台快捷命令
window.pdf = {
  refresh: ProductDetailPriceFetcher.refresh,
  variant: ProductDetailPriceFetcher.getCurrentVariant,
  clearCache: ProductDetailPriceFetcher.clearCache,
  test: ProductDetailPriceFetcher.testVariant,
  cache: ProductDetailPriceFetcher.getCache
};

console.log('💰 ProductDetailPriceFetcher 已加载！使用 pdf.refresh() 刷新价格，pdf.test(variantId) 测试单个变体');